import { Link } from "@tanstack/react-router";
import { Dumbbell, Video } from "lucide-react";

import { ClockRing } from "../../components/clock-ring";
import { CoachPhoto } from "../../components/coach-photo";
import { DotRing } from "../../components/dot-ring";
import {
  Section,
  SectionHead,
  card,
  figure,
  ghostButton,
  inkButton,
  inkTag,
  label,
  pill,
  primaryCard,
  well,
} from "../../components/section";
import { ServiceGrid } from "../../components/service-card";
import { PageTitle, SiteHead, SiteNav } from "../../components/site-head";
import { SitePage } from "../../components/site-page";
import { glowClass } from "../../components/site-theme";
import { SplitSection } from "../../components/split-section";
import { TimeChip } from "../../components/time-chip";
import { usePublicDirectory } from "../../hooks/use-public-directory";
import { contactOf, sitePath, useSiteConfig } from "../../hooks/use-site-config";
import type { PublicDirectory, PublicService } from "../../types";
import { openTimes, soonestOpening, specialistsFor } from "../../utils/directory";
import { coachAnchor, minutesLabel, openTimeLabel, reachOutLabel, servicePriceLabel } from "../../utils/labels";
import { SlotGateDialog } from "../open-calendar/slot-gate-dialog";
import { useSlotGate } from "../open-calendar/use-slot-gate";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingMarkdown } from "@/features/booking/modules/markdown/markdown";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Open times shown per coach under "Who coaches it". */
const TIMES_PER_COACH = 3;
/** Other services offered at the bottom of the page. */
const RELATED = 3;

/** The one action on the solid card, as wide as the card. */
const inkButtonWide = cn(inkButton, "h-[54px] w-full text-[15px]");
const heading = "text-[clamp(2.75rem,6.4vw,5.25rem)] leading-[0.96] font-medium tracking-[-0.05em]";

/** The page for a service the trainer does not have. */
export const ServiceMissing = () => {
  const site = useSiteConfig();

  return (
    <SitePage
      head={
        <SiteHead>
          <PageTitle title={m.services_missing_title()} aside={m.services_missing_body()} />
          <Link to={sitePath(site.slug, "services")} className={cn(ghostButton, "mt-8")}>
            {m.services_all()}
          </Link>
        </SiteHead>
      }
    >
      {null}
    </SitePage>
  );
};

const Fact = ({ title, value }: { title: string; value: string | null }) =>
  value ? (
    <div className={cn(well, "min-w-[132px] px-5 py-4")}>
      <dt className={label}>{title}</dt>
      <dd className={cn(figure, "mt-2 text-[28px] whitespace-nowrap")}>{value}</dd>
    </div>
  ) : null;

const Loading = () => (
  <SitePage
    head={
      <>
        <SiteNav />
        <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Skeleton className="min-h-[420px] rounded-[28px] lg:min-h-[520px]" />
          <Skeleton className="min-h-[420px] rounded-[28px]" />
        </div>
      </>
    }
  >
    {null}
  </SitePage>
);

const Details = ({ service, catalog }: { service: PublicService; catalog: PublicDirectory }) => {
  const site = useSiteConfig();
  const contact = contactOf(site);
  // Open times depend on how long a session takes, so the coaches and their times are asked for
  // this service.
  const { directory: scoped } = usePublicDirectory({ days: site.calendarDays, sessionTypeId: service.id });
  const directory = scoped ?? catalog;
  const coaches = specialistsFor([service], directory);
  const { open, gate } = useSlotGate(directory);

  const Icon = service.online ? Video : Dumbbell;
  const price = servicePriceLabel(service, catalog.business.currency);
  const next = soonestOpening(coaches);
  const related = catalog.services.filter((other) => other.id !== service.id).slice(0, RELATED);

  return (
    <SitePage
      head={
        <>
          <SiteNav />
          <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className={cn(card, glowClass, "flex flex-col justify-between gap-10 md:p-11 lg:min-h-[520px]")}>
              <Link to={sitePath(site.slug, "services")} className={cn(pill, "max-w-full self-start")}>
                <span className="truncate">
                  {m.landing_nav_services()} / {service.name}
                </span>
              </Link>
              <div>
                <h1 className={heading}>{service.name}</h1>
                {service.description ? (
                  <BookingMarkdown
                    source={service.description}
                    className="mt-5 max-w-[52ch] text-[17px] text-muted-foreground"
                  />
                ) : null}
                <dl className="mt-8 flex flex-wrap gap-2">
                  <Fact title={m.services_fact_length()} value={minutesLabel(service.durationMinutes)} />
                  <Fact title={m.services_fact_price()} value={price} />
                </dl>
              </div>
            </div>

            <div className={cn(primaryCard, "flex flex-col items-center text-center md:pt-9")}>
              <span className={cn(inkTag, "self-center")}>{m.services_fact_next()}</span>
              {next ? (
                <ClockRing
                  startAt={next.startAt}
                  timeZone={next.specialist.timeZone}
                  tone="primary"
                  className="mt-3 mb-6 size-[220px]"
                />
              ) : (
                <DotRing filled={0} total={32} dot={2.6} tone="primary" className="mt-3 mb-6 size-[220px]">
                  <Icon aria-hidden className="size-10" strokeWidth={1.5} />
                </DotRing>
              )}
              <div className="mb-6">
                <p className="text-[22px] font-semibold tracking-[-0.02em]">
                  {next ? next.specialist.name : m.services_no_times_title()}
                </p>
                <p className="text-[var(--site-on-primary-soft)]">
                  {next ? next.specialist.title : reachOutLabel(contact)}
                </p>
              </div>
              {next ? (
                <button
                  type="button"
                  className={cn(inkButtonWide, "mt-auto")}
                  onClick={() =>
                    open({ specialistId: next.specialist.id, serviceId: service.id, startAt: next.startAt })
                  }
                >
                  {m.landing_nav_book()}
                </button>
              ) : contact ? (
                <a href={contact.href} className={cn(inkButtonWide, "mt-auto")}>
                  {contact.label}
                </a>
              ) : null}
            </div>
          </div>
        </>
      }
    >
      {coaches.length > 0 ? (
        <SplitSection
          title={
            <>
              {m.services_who_title_lead()}
              <br />
              {m.services_who_title_tail()}
            </>
          }
        >
          <div className="grid gap-3">
            {coaches.map((coach) => (
              <div
                key={coach.id}
                className={cn(
                  card,
                  "grid grid-cols-[auto_1fr] items-center gap-4 p-4 md:p-4 lg:grid-cols-[auto_1fr_auto] lg:pr-6",
                )}
              >
                <CoachPhoto name={coach.name} src={coach.avatarUrl} className="size-16 rounded-[20px] text-xl" />
                <Link
                  to={sitePath(site.slug, "coaches")}
                  hash={coachAnchor(coach.id)}
                  className="min-w-0 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <b className="block truncate text-[17px] font-medium tracking-[-0.01em]">{coach.name}</b>
                  <span className="block truncate text-[var(--site-ink)]">{coach.title}</span>
                </Link>
                <div className="col-span-full flex flex-wrap gap-1.5 lg:col-span-1 lg:justify-end">
                  {openTimes(coach)
                    .slice(0, TIMES_PER_COACH)
                    .map((startAt) => (
                      <TimeChip
                        key={startAt}
                        onClick={() => open({ specialistId: coach.id, serviceId: service.id, startAt })}
                      >
                        {openTimeLabel(startAt, directory)}
                      </TimeChip>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </SplitSection>
      ) : null}

      {related.length > 0 ? (
        <Section>
          <SectionHead
            title={
              <>
                {m.services_related_title_lead()}
                <br />
                {m.services_related_title_tail()}
              </>
            }
            aside={
              <Link to={sitePath(site.slug, "services")} className={cn(ghostButton, "text-foreground")}>
                {m.services_all()}
              </Link>
            }
          />
          <ServiceGrid services={related} directory={catalog} />
        </Section>
      ) : null}

      <SlotGateDialog {...gate} />
    </SitePage>
  );
};

/**
 * One of the trainer's booking services in full. The address carries the service's id, so a
 * service renamed in Setup keeps its page; an id the trainer does not have gets a short "not
 * here" page with the way back.
 */
export const ServiceDetail = ({ serviceId }: { serviceId: string }) => {
  const site = useSiteConfig();
  const { directory, loading } = usePublicDirectory({ days: site.calendarDays });
  const service = directory?.services.find((candidate) => candidate.id === serviceId) ?? null;

  if (loading) return <Loading />;
  if (!directory || !service) return <ServiceMissing />;
  return <Details key={service.id} service={service} catalog={directory} />;
};
